import React, { useState } from 'react';
import { AttributeGroup } from '../../../../api/protosCompiled/globalItem/globalItem_pb';
import { UB_AccountUser } from '../../../../api/protosCompiled/ub_users/ub_users_pb';
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useGetMasterFormatDivision } from '../../../../hooks/useGetMasterFormatDivision';

interface AttributesTableProps {
  attributeGroups: AttributeGroup.AsObject[];
  user: UB_AccountUser.AsObject | null;
  onEdit: (attributeGroup: AttributeGroup.AsObject) => void;
  onDelete: (attributeGroupId: number) => void;
}

const AttributesTable: React.FC<AttributesTableProps> = ({
  attributeGroups,
  user,
  onEdit,
  onDelete,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDivision, setFilterDivision] = useState('');
  const theme = useTheme();

  const { data: masterFormatDivision } = useGetMasterFormatDivision();

  // Create divisions array for select
  const availableDivisions = masterFormatDivision
    ? masterFormatDivision.filter(division =>
        attributeGroups.some(group => group.masterformatid.toString() === division.id.toString())
      )
    : [];

  const selectStyles = {
    '& .MuiSelect-select': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
    },
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
    },
  };

  const filteredGroups = attributeGroups.filter(
    group =>
      group.attributegroupname.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (!filterDivision ||
        filterDivision === '' ||
        group.masterformatid.toString() === filterDivision)
  );

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        width: '100%',
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
      }}
    >
      <div
        style={{
          padding: '8px',
          flexShrink: 0,
          background: theme.palette.background.paper,
          display: 'flex',
          gap: '8px',
        }}
      >
        <TextField
          size="small"
          placeholder="Search attribute groups..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: theme.palette.background.paper,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          aria-label="Search attribute groups"
          inputProps={{
            'aria-label': 'Search attribute groups input',
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary }} />,
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="filter-division-label">Filter by Division</InputLabel>
          <Select
            labelId="filter-division-label"
            value={filterDivision}
            onChange={e => setFilterDivision(e.target.value)}
            label="Filter by Division"
            size="small"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
            sx={selectStyles}
          >
            <MenuItem value="">All Divisions</MenuItem>
            {availableDivisions.length > 0 ? (
              availableDivisions.map(division => (
                <MenuItem key={division.id} value={division.id.toString()}>
                  {`${division.divisionnumber} - ${division.divisionname}`}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No divisions available</MenuItem>
            )}
          </Select>
        </FormControl>
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Table
          aria-label="Attribute groups table"
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableRow-root': {
              '&:nth-of-type(odd)': {
                bgcolor:
                  theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
              },
              '&:nth-of-type(even)': {
                bgcolor:
                  theme.palette.mode === 'dark' ? '#3c3c3c !important' : '#e0e0e0 !important',
              },
              '&:hover': {
                bgcolor: `${
                  theme.palette.mode === 'dark'
                    ? 'rgba(33, 150, 243, 0.1)'
                    : 'rgba(33, 150, 243, 0.1)'
                } !important`,
              },
            },
            '& .MuiTableCell-root': {
              padding: '8px 16px',
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
            },
            '& .MuiTableHead-root': {
              '& .MuiTableCell-root': {
                bgcolor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f5f5f5',
                color: theme.palette.text.primary,
                fontWeight: 600,
              },
            },
          }}
        >
          <TableBody>
            {user && filteredGroups.length > 0 ? (
              filteredGroups
                .sort((a, b) => a.attributegroupname.localeCompare(b.attributegroupname))
                .map((attributeGroup, index) => (
                  <TableRow
                    key={attributeGroup.attributegroupid}
                    hover
                    tabIndex={0}
                    aria-label={`Attribute group: ${attributeGroup.attributegroupname}`}
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? index % 2 === 0
                            ? '#2c2c2c'
                            : '#3c3c3c'
                          : index % 2 === 0
                            ? '#f5f5f5'
                            : '#e0e0e0',
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{ color: theme.palette.text.primary }}
                          >
                            {attributeGroup.attributegroupname}
                          </Typography>
                          <Box>
                            <Tooltip title="Edit attribute group">
                              <IconButton
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onEdit(attributeGroup);
                                }}
                                aria-label={`Edit ${attributeGroup.attributegroupname}`}
                                size="small"
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    bgcolor: `rgba(33, 150, 243, 0.1)`,
                                  },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete attribute group">
                              <IconButton
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onDelete(attributeGroup.attributegroupid);
                                }}
                                aria-label={`Delete ${attributeGroup.attributegroupname}`}
                                size="small"
                                sx={{
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    bgcolor: `rgba(244, 67, 54, 0.1)`,
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            pl: 1,
                            color: theme.palette.text.secondary,
                            opacity: 0.6,
                          }}
                        >
                          Division:{' '}
                          {(() => {
                            const division = masterFormatDivision?.find(
                              division => division.id === attributeGroup.masterformatid
                            );
                            return division
                              ? `${division.divisionnumber} : ${division.divisionname}`
                              : 'Not assigned';
                          })()}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f5f5f5',
                    color: theme.palette.text.secondary,
                  }}
                >
                  No attribute groups found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
};

export default React.memo(AttributesTable);
